import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import {
    isPushSupported,
    getCurrentSubscription,
    subscribeForPush,
    registerServiceWorker,
} from '../../services/webPush';

type BannerState =
    | 'loading'
    | 'unsupported'
    | 'idle'         // permission default, not subscribed — show CTA
    | 'subscribed'   // permission granted + subscription registered — hide
    | 'denied';      // user clicked "block" in browser — explain how to unblock

/**
 * Top-of-archive-page card prompting the user to opt into browser-level push
 * notifications. Visible only when there's an action available — once the
 * user has subscribed, the banner hides itself (no need for a "✓ Active"
 * permanent reminder, that lives in the Settings page).
 *
 * State transitions:
 *   loading → unsupported   browser lacks Push/SW/Notification APIs
 *   loading → idle          permission=default, no subscription yet
 *   loading → subscribed    permission=granted, subscription exists (hide)
 *   loading → denied        permission=denied (user clicked Block previously)
 *   idle    → subscribed    after successful subscribe
 *   idle    → denied        if user clicks Block on the native prompt
 */
const WebPushBanner: React.FC = () => {
    const [state, setState] = useState<BannerState>('loading');
    const [busy, setBusy] = useState(false);
    const intl = useIntl();

    // Register the SW on mount even if the user hasn't clicked Enable yet —
    // if they subscribed in a previous session, the worker needs to be alive
    // to receive incoming pushes.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!isPushSupported()) {
                if (!cancelled) setState('unsupported');
                return;
            }
            await registerServiceWorker();
            const sub = await getCurrentSubscription();
            if (cancelled) return;
            if (sub) {
                setState('subscribed');
                return;
            }
            if (Notification.permission === 'denied') {
                setState('denied');
            } else {
                setState('idle');
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleEnable = useCallback(async () => {
        setBusy(true);
        const result = await subscribeForPush();
        setBusy(false);

        if (result.status === 'ok') {
            setState('subscribed');
            toast.success(intl.formatMessage({
                id: 'notifications.webpush.successToast',
                defaultMessage: 'Browser notifications enabled on this device',
            }));
            return;
        }

        if (result.status === 'permission_denied') {
            setState('denied');
            return;
        }

        if (result.status === 'unsupported') {
            setState('unsupported');
            return;
        }

        // Network/subscribe/sw failure — toast and stay idle so the user can retry.
        toast.error(intl.formatMessage({
            id: 'notifications.webpush.errorToast',
            defaultMessage: 'Could not enable browser notifications. Please try again.',
        }));
    }, [intl]);

    if (state === 'loading' || state === 'subscribed' || state === 'unsupported') {
        return null;
    }

    if (state === 'denied') {
        return (
            <div className="webpush-banner webpush-banner--denied">
                <span className="webpush-banner__icon" aria-hidden="true">🔕</span>
                <div className="webpush-banner__body">
                    <strong className="webpush-banner__title">
                        <FormattedMessage
                            id="notifications.webpush.deniedTitle"
                            defaultMessage="Browser notifications are blocked"
                        />
                    </strong>
                    <span className="webpush-banner__desc">
                        <FormattedMessage
                            id="notifications.webpush.deniedDesc"
                            defaultMessage="To re-enable, click the lock icon next to the URL in your browser and allow notifications."
                        />
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="webpush-banner">
            <span className="webpush-banner__icon" aria-hidden="true">🔔</span>
            <div className="webpush-banner__body">
                <strong className="webpush-banner__title">
                    <FormattedMessage
                        id="notifications.webpush.enableTitle"
                        defaultMessage="Get notified instantly"
                    />
                </strong>
                <span className="webpush-banner__desc">
                    <FormattedMessage
                        id="notifications.webpush.enableDesc"
                        defaultMessage="Browser notifications work even when this tab is closed."
                    />
                </span>
            </div>
            <button
                type="button"
                className="webpush-banner__cta"
                onClick={handleEnable}
                disabled={busy}
            >
                <FormattedMessage
                    id={busy ? 'notifications.webpush.enablingButton' : 'notifications.webpush.enableButton'}
                    defaultMessage={busy ? 'Enabling…' : 'Enable'}
                />
            </button>
        </div>
    );
};

export default WebPushBanner;
